import type { LocalizedDict } from '../index'

type BulkCopy = { title: string; body: (n: number) => string; confirm: string }

export type SampleRequestsListStrings = {
  skipReasons: Record<'not_found' | 'in_trash' | 'already_received', string>
  stats: {
    inTrash: string
    drafts: string
    other: string
    total: string
    readyForFactory: string
    inProduction: string
  }
  titleTrash: string
  titleReady: string
  titleAll: string
  subTrash: string
  subReady: string
  subAll: string
  bulkFailed: string
  tryAgain: string
  skippedDetail: (count: number, reasons: string) => string
  nothingReceived: string
  markedReceived: (n: number) => string
  bulk: {
    delete: BulkCopy
    restore: BulkCopy
    received: BulkCopy
    purge: BulkCopy
  }
  trashTitle: (n: number) => string
  trash: string
  newRequest: string
  backToSamples: string
  clear: string
  selected: (n: number) => string
  exporting: string
  exportAs: string
  excel: string
  markAsReceived: string
  adoptableTitle: (n: number) => string
  adoptableNone: string
  addToCatalog: (n: number) => string
  moveToTrash: string
  restore: string
  deleteForever: string
  allSamples: string
  allStatuses: string
  allProducers: string
  catalogAny: string
  inCatalog: string
  notInCatalog: string
  hideCompleted: string
  searchPlaceholder: string
  loading: string
  empty: string
  selectAll: string
  cols: {
    code: string
    name: string
    material: string
    size: string
    color: string
    priority: string
    activity: string
    status: string
    catalogue: string
    producer: string
    productionDate: string
    deleted: string
    lastUpdated: string
  }
  selectRow: (title: string) => string
  untitled: string
  references: (n: number) => string
  briefPhotos: (n: number) => string
  inCatalogue: string
  notInCatalogue: string
  unassigned: string
  daysOverdue: (n: number) => string
  sample: string
  duplicate: string
  permanentlyDelete: string
  moveToTrashQ: string
  deleteForeverBody: (title: string) => string
  moveToTrashBody: (title: string) => string
  cancel: string
}

export const sampleRequestsListI18n: LocalizedDict<SampleRequestsListStrings> = {
  en: {
    skipReasons: {
      not_found: 'no longer exists',
      in_trash: 'in trash',
      already_received: 'already received',
    },
    stats: {
      inTrash: 'In Trash',
      drafts: 'Drafts',
      other: 'Other',
      total: 'Total',
      readyForFactory: 'Ready for Factory',
      inProduction: 'In Production',
    },
    titleTrash: 'Trash',
    titleReady: 'Ready for Factory',
    titleAll: 'Sample Requests',
    subTrash: 'Soft-deleted samples. Restore them or permanently delete.',
    subReady: 'Samples being worked on with the factory.',
    subAll: 'All your sample requests — drafts, in factory, and confirmed.',
    bulkFailed: 'Bulk action failed',
    tryAgain: 'Please try again.',
    skippedDetail: (count, reasons) => `${count} skipped (${reasons}).`,
    nothingReceived: 'Nothing marked as received',
    markedReceived: (n) => `${n} marked as received`,
    bulk: {
      delete: {
        title: 'Move to trash?',
        body: (n) => `${n} item(s) will be moved to trash.`,
        confirm: 'Move to trash',
      },
      restore: {
        title: 'Restore?',
        body: (n) => `${n} item(s) will be restored.`,
        confirm: 'Restore',
      },
      received: {
        title: 'Samples received?',
        body: (n) =>
          `${n} sample(s) will move straight to Received. Any factory steps they never went through are overridden and recorded in each activity log. This unlocks adding them to the catalog. Rows already received or in trash are skipped.`,
        confirm: 'Mark as received',
      },
      purge: {
        title: 'Permanently delete?',
        body: (n) => `${n} item(s) will be deleted forever.`,
        confirm: 'Delete forever',
      },
    },
    trashTitle: (n) => `Trash (${n})`,
    trash: 'Trash',
    newRequest: 'New Request',
    backToSamples: '← Back to samples',
    clear: 'Clear',
    selected: (n) => `${n} selected`,
    exporting: 'Exporting…',
    exportAs: 'Export as',
    excel: 'Excel (XLS)',
    markAsReceived: 'Mark as received',
    adoptableTitle: (n) => `${n} of the selected sample(s) can go to the catalog`,
    adoptableNone: 'Select received samples that are not in the catalog yet',
    addToCatalog: (n) => `Add to catalog (${n})`,
    moveToTrash: 'Move to trash',
    restore: 'Restore',
    deleteForever: 'Delete forever',
    allSamples: 'All samples',
    allStatuses: 'All statuses',
    allProducers: 'All producers',
    catalogAny: 'Catalog: any',
    inCatalog: 'In catalog',
    notInCatalog: 'Not in catalog',
    hideCompleted: 'Hide completed',
    searchPlaceholder: 'Search title, SS code, producer',
    loading: 'Loading…',
    empty: 'No samples here yet.',
    selectAll: 'Select all',
    cols: {
      code: 'Sample Code',
      name: 'Sample Name',
      material: 'Material',
      size: 'Size',
      color: 'Color',
      priority: 'Priority',
      activity: 'Activity',
      status: 'Status',
      catalogue: 'Catalogue',
      producer: 'Producer',
      productionDate: 'Production Date',
      deleted: 'Deleted',
      lastUpdated: 'Last updated',
    },
    selectRow: (title) => `Select ${title}`,
    untitled: 'Untitled',
    references: (n) => `${n} references`,
    briefPhotos: (n) => `${n} brief photos`,
    inCatalogue: 'In catalogue',
    notInCatalogue: 'Not in catalogue',
    unassigned: 'Unassigned',
    daysOverdue: (n) => `${n}d overdue`,
    sample: 'Sample',
    duplicate: 'Duplicate',
    permanentlyDelete: 'Permanently delete?',
    moveToTrashQ: 'Move to trash?',
    deleteForeverBody: (title) => `"${title}" will be deleted forever. This cannot be undone.`,
    moveToTrashBody: (title) => `"${title}" will be moved to trash. You can restore it later.`,
    cancel: 'Cancel',
  },
  tr: {
    skipReasons: {
      not_found: 'artık yok',
      in_trash: 'çöp kutusunda',
      already_received: 'zaten teslim alınmış',
    },
    stats: {
      inTrash: 'Çöp kutusunda',
      drafts: 'Taslaklar',
      other: 'Diğer',
      total: 'Toplam',
      readyForFactory: 'Fabrikaya hazır',
      inProduction: 'Üretimde',
    },
    titleTrash: 'Çöp Kutusu',
    titleReady: 'Fabrikaya hazır',
    titleAll: 'Numune talepleri',
    subTrash: 'Silinen numuneler. Geri yükle ya da kalıcı olarak sil.',
    subReady: 'Fabrikayla üzerinde çalışılan numuneler.',
    subAll: 'Tüm numune taleplerin — taslaklar, fabrikadakiler ve onaylananlar.',
    bulkFailed: 'Toplu işlem başarısız',
    tryAgain: 'Lütfen tekrar dene.',
    skippedDetail: (count, reasons) => `${count} atlandı (${reasons}).`,
    nothingReceived: 'Hiçbiri teslim alındı işaretlenmedi',
    markedReceived: (n) => `${n} numune teslim alındı işaretlendi`,
    bulk: {
      delete: {
        title: 'Çöp kutusuna taşınsın mı?',
        body: (n) => `${n} kayıt çöp kutusuna taşınacak.`,
        confirm: 'Çöp kutusuna taşı',
      },
      restore: {
        title: 'Geri yüklensin mi?',
        body: (n) => `${n} kayıt geri yüklenecek.`,
        confirm: 'Geri yükle',
      },
      received: {
        title: 'Numuneler teslim alındı mı?',
        body: (n) =>
          `${n} numune doğrudan Teslim alındı’ya geçecek. Geçmedikleri fabrika adımları atlanır ve her birinin hareket kaydına yazılır. Böylece kataloğa eklenebilirler. Zaten teslim alınmış ya da çöp kutusundaki satırlar atlanır.`,
        confirm: 'Teslim alındı işaretle',
      },
      purge: {
        title: 'Kalıcı olarak silinsin mi?',
        body: (n) => `${n} kayıt kalıcı olarak silinecek.`,
        confirm: 'Kalıcı olarak sil',
      },
    },
    trashTitle: (n) => `Çöp Kutusu (${n})`,
    trash: 'Çöp Kutusu',
    newRequest: 'Yeni talep',
    backToSamples: '← Numunelere dön',
    clear: 'Temizle',
    selected: (n) => `${n} seçili`,
    exporting: 'Dışa aktarılıyor…',
    exportAs: 'Dışa aktar',
    excel: 'Excel (XLS)',
    markAsReceived: 'Teslim alındı işaretle',
    adoptableTitle: (n) => `Seçili numunelerden ${n} tanesi kataloğa eklenebilir`,
    adoptableNone: 'Henüz katalogda olmayan, teslim alınmış numuneleri seç',
    addToCatalog: (n) => `Kataloğa ekle (${n})`,
    moveToTrash: 'Çöp kutusuna taşı',
    restore: 'Geri yükle',
    deleteForever: 'Kalıcı olarak sil',
    allSamples: 'Tüm numuneler',
    allStatuses: 'Tüm durumlar',
    allProducers: 'Tüm üreticiler',
    catalogAny: 'Katalog: hepsi',
    inCatalog: 'Katalogda',
    notInCatalog: 'Katalogda değil',
    hideCompleted: 'Tamamlananları gizle',
    searchPlaceholder: 'Başlık, SS kodu, üretici ara',
    loading: 'Yükleniyor…',
    empty: 'Burada henüz numune yok.',
    selectAll: 'Tümünü seç',
    cols: {
      code: 'Numune kodu',
      name: 'Numune adı',
      material: 'Malzeme',
      size: 'Ölçü',
      color: 'Renk',
      priority: 'Öncelik',
      activity: 'Hareket',
      status: 'Durum',
      catalogue: 'Katalog',
      producer: 'Üretici',
      productionDate: 'Üretim tarihi',
      deleted: 'Silinme',
      lastUpdated: 'Son güncelleme',
    },
    selectRow: (title) => `${title} seç`,
    untitled: 'Adsız',
    references: (n) => `${n} referans`,
    briefPhotos: (n) => `${n} brif fotoğrafı`,
    inCatalogue: 'Katalogda',
    notInCatalogue: 'Katalogda değil',
    unassigned: 'Atanmadı',
    daysOverdue: (n) => `${n} gün gecikti`,
    sample: 'Numune',
    duplicate: 'Kopyala',
    permanentlyDelete: 'Kalıcı olarak silinsin mi?',
    moveToTrashQ: 'Çöp kutusuna taşınsın mı?',
    deleteForeverBody: (title) => `"${title}" kalıcı olarak silinecek. Bu işlem geri alınamaz.`,
    moveToTrashBody: (title) => `"${title}" çöp kutusuna taşınacak. Daha sonra geri yükleyebilirsin.`,
    cancel: 'Vazgeç',
  },
}
